export type TipoMeta = 'VALOR' | 'QUANTIDADE' | 'MEDIA_DIAS_ABERTO' | 'PERCENTUAL_STATUS' | 'PROCESSOS_PENDENTES' | 'TAXA_SAIDA_ENTRADA';

export interface TipoMetaConfig {
  value: TipoMeta;
  label: string;
  descricaoCurta: string;
  icon: string;
  labelAtingido: string;
  labelMeta: string;
  labelRestante: string;
  formatValor: (v: number) => string;
}

const formatMoeda = (v: number) => new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(v);
const formatInteiro = (v: number, sufixo: string) => `${new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 0}).format(v)} ${sufixo}`;
const formatDecimal = (v: number, sufixo: string) => `${new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 1}).format(v)} ${sufixo}`;

export const TIPO_META_CONFIG: Record<TipoMeta, TipoMetaConfig> = {
  VALOR: {
    value: 'VALOR',
    label: 'Valor (R$)',
    descricaoCurta: 'Meta baseada na soma do valor atual das notificações no período.',
    icon: 'pi pi-dollar',
    labelAtingido: 'Valor Atingido',
    labelMeta: 'Meta Total',
    labelRestante: 'Valor Restante',
    formatValor: formatMoeda,
  },
  QUANTIDADE: {
    value: 'QUANTIDADE',
    label: 'Quantidade de notificações',
    descricaoCurta: 'Meta baseada no número de notificações que atingem os status contribuintes no período.',
    icon: 'pi pi-hashtag',
    labelAtingido: 'Notificações Atingidas',
    labelMeta: 'Meta de Notificações',
    labelRestante: 'Notificações Restantes',
    formatValor: v => formatInteiro(v, 'notificações'),
  },
  MEDIA_DIAS_ABERTO: {
    value: 'MEDIA_DIAS_ABERTO',
    label: 'Média de dias em aberto',
    descricaoCurta: 'Meta de agilidade: quanto menor a média de dias em aberto, melhor. O valor é o teto desejado.',
    icon: 'pi pi-clock',
    labelAtingido: 'Média de Dias Atual',
    labelMeta: 'Limite de Dias',
    labelRestante: 'Dias a Reduzir',
    formatValor: v => formatDecimal(v, 'dias'),
  },
  PERCENTUAL_STATUS: {
    value: 'PERCENTUAL_STATUS',
    label: 'Percentual em um status',
    descricaoCurta: 'Meta de eficácia: percentual das notificações contribuintes que chegam a um status-alvo específico.',
    icon: 'pi pi-percentage',
    labelAtingido: '% Atingido',
    labelMeta: '% Alvo',
    labelRestante: 'Pontos % Restantes',
    formatValor: v => formatDecimal(v, '%'),
  },
  PROCESSOS_PENDENTES: {
    value: 'PROCESSOS_PENDENTES',
    label: 'Processos pendentes (apuração por corte)',
    descricaoCurta: 'Conta notificações do curso alvo com status contribuinte e NOT emitida até a data de corte (Data Fim).',
    icon: 'pi pi-hashtag',
    labelAtingido: 'Processos Pendentes',
    labelMeta: 'Meta de Processos',
    labelRestante: 'Processos Restantes',
    formatValor: v => formatInteiro(v, 'processos'),
  },
  TAXA_SAIDA_ENTRADA: {
    value: 'TAXA_SAIDA_ENTRADA',
    label: 'Taxa Saída/Entrada',
    descricaoCurta: 'Compara quantas notificações saíram (status contribuintes, pela data de Andamento) frente às que entraram (NOT emitida) no período. A meta é o percentual mínimo aceitável de saída em relação à entrada.',
    icon: 'pi pi-arrows-h',
    labelAtingido: 'Saída/Entrada Atual',
    labelMeta: '% Mínimo Aceitável',
    labelRestante: 'Pontos % Restantes',
    formatValor: v => formatDecimal(v, '%'),
  },
};

export interface MetaResponse {
  id: number;
  nome: string;
  descricao?: string;
  valorMeta: number;
  dataInicio: string;
  dataFim: string;
  statusContribuintes: string[];
  ativo: boolean;
  tipoMeta: TipoMeta;
  statusAlvo?: string | null;
  cursoAlvo?: string | null;
}

export interface MetaRequest {
  nome: string;
  descricao?: string;
  valorMeta: number;
  dataInicio: string;
  dataFim: string;
  statusContribuintes: string[];
  ativo: boolean;
  tipoMeta: TipoMeta;
  statusAlvo?: string | null;
  cursoAlvo?: string | null;
}

export interface MetaConcessionaria {
  concessionaria: string;
  quantidade: number;
  valorContribuido: number;
  percentualDaMeta: number;
}

export interface MetaGrupo {
  chave: string;
  quantidade: number;
  valorContribuido: number;
  percentualDaMeta: number;
}

export type MetaDimensao = 'concessionaria' | 'status' | 'fase' | 'motivo' | 'curso';

export type StatusProjecao = 'ATINGIDA' | 'NO_CAMINHO' | 'FORA_DO_RITMO' | 'ENCERRADA';

export interface MetaProjecao {
  ritmoMedioAtual: number;
  projecaoFinal: number;
  percentualProjetado: number;
  ritmoNecessario: number | null;
  status: StatusProjecao;
}

export interface MetaProgresso {
  id: number;
  nome: string;
  descricao?: string;
  valorMeta: number;
  valorAtingido: number;
  valorRestante: number;
  percentualAtingido: number;
  dataInicio: string;
  dataFim: string;
  diasRestantes: number;
  totalNotificacoes: number;
  statusContribuintes: string[];
  porConcessionaria: MetaConcessionaria[];
  agrupamentos: Record<MetaDimensao, MetaGrupo[]>;
  tipoMeta: TipoMeta;
  statusAlvo?: string | null;
  cursoAlvo?: string | null;
  projecao: MetaProjecao | null;
}
